import type {
  AIAgentMode,
  AIRetrievalMode,
  AIToolsConfig,
} from '@affine/core/modules/ai-button';
import {
  classifyAssistantTask,
  isFocusedAttachedDatabaseSchemaEdit,
} from '@streamlain/assistant-routing-contract';

export interface CopilotRetrievalPlan {
  mode: AIRetrievalMode;
  webSearch: boolean;
  toolsConfig: AIToolsConfig;
}

const SMALLTALK_OR_TEST_RE =
  /^(?:hi|hey|hello|hallo|moin|servus|test|ping|ok|okay|danke|thanks|yo|sup)[.!?\s]*$/iu;

const WEB_INTENT_RE =
  /\b(?:web|internet|online|aktuell|latest|heute|today|news|preise|price|weather|wetter|kurs|stock|boerse|börse|recherchier|research online)\b/iu;

export type WebSearchToggleMode = 'auto' | 'off';
export type TargetModeProposalDomain = 'custom_app' | 'automation';

export interface TargetModeProposal {
  domain: TargetModeProposalDomain;
  reason: string;
  confidence: number;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function shouldAutoSearchWeb(input: string): boolean {
  const text = normalizeText(input);
  if (!text || SMALLTALK_OR_TEST_RE.test(text)) {
    return false;
  }
  return WEB_INTENT_RE.test(text);
}

export function resolveTargetModeProposal(
  input: string,
  _agentMode: AIAgentMode,
  context?: {
    hasAttachedAutomation?: boolean;
    hasAttachedDatabase?: boolean;
  }
): TargetModeProposal | null {
  const text = normalizeText(input);
  if (!text || SMALLTALK_OR_TEST_RE.test(text)) {
    return null;
  }

  const classification = classifyAssistantTask(text);
  if (
    context?.hasAttachedAutomation &&
    context.hasAttachedDatabase &&
    isFocusedAttachedDatabaseSchemaEdit(text)
  ) {
    return null;
  }
  if (!classification.domain || !classification.durableTargetCandidate) {
    return null;
  }
  return {
    domain: classification.domain,
    reason:
      classification.domain === 'automation'
        ? 'Dieses Target braucht Planung, Ausführung und Prüfung.'
        : 'Dieses Target braucht mehrere Schritte, Prüfungen und mögliche Rückfragen.',
    confidence: classification.confidence,
  };
}

export function shouldProposeAgentMode(input: string): boolean {
  const text = normalizeText(input);
  if (!text || SMALLTALK_OR_TEST_RE.test(text)) return false;
  const classification = classifyAssistantTask(text);
  return Boolean(
    classification.domain && classification.durableTargetCandidate
  );
}

export function resolveCopilotRetrievalPlan(
  input: string,
  config: AIToolsConfig,
  networkSearchVisible: boolean
): CopilotRetrievalPlan {
  const configuredMode = config.retrievalMode ?? 'auto';
  const mode =
    configuredMode === 'workspace'
      ? 'off'
      : configuredMode === 'workspace-web'
        ? 'auto'
        : configuredMode;
  const webRequested =
    networkSearchVisible &&
    (mode === 'web' || (mode === 'auto' && shouldAutoSearchWeb(input)));

  return {
    mode,
    webSearch: webRequested,
    toolsConfig: {
      ...config,
      // Workspace evidence is derived server-side from the versioned intent
      // contract. It is deliberately not a user-visible retrieval option.
      searchWorkspace: false,
      readingDocs: false,
    },
  };
}

export function nextRetrievalModeForWebToggle(
  _current: AIRetrievalMode,
  enabled: boolean
): AIRetrievalMode {
  if (enabled) {
    return 'web';
  }
  return 'off';
}

export function webSearchToggleModeForRetrievalMode(
  current: AIRetrievalMode
): WebSearchToggleMode {
  if (current === 'off' || current === 'workspace') {
    return 'off';
  }
  return 'auto';
}

export function setWebSearchToggleMode(
  _current: AIRetrievalMode,
  mode: WebSearchToggleMode
): AIRetrievalMode {
  if (mode === 'off') {
    return 'off';
  }
  return 'auto';
}
